import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Tab, Tabs, Theme, Typography, Button } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { StringParam, useQueryParam } from 'use-query-params';
import { ArrowBack, Edit } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { unpackToDateTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useOrgInfo } from '../../../../global/auth/use-org-info/use-org-info';
import { useUser } from '../../../../global/auth/useUser';
import { GET_FORMS, GET_FORM_SUBMISSIONS_LIST, GET_FORM_SUBMISSION, GET_STARRED_FORM_FIELDS } from '../forms.gql';
import { CREATE_ROSTER_TEMPLATE_SCREEN_GET_PROGRAMS_AND_ACTIVITIES } from '../../../program-manager/rosters/roster-templates-screen/roster-template-model.gql';
import {
  FormType,
  FormSubmissionListItem,
  FormFieldConfig,
  AvailableFormField,
  ChartDataPoint,
  FieldAnalytics,
  EntityType,
  PAGE_SIZE,
  GetFormSubmissionsList,
  GetFormSubmissionsListVariables,
  GetFormSubmission,
  GetFormSubmissionVariables,
  CreateRosterTemplateScreenGetProgramsAndActivities,
} from './form-responses-types';
import { escapeCSV, buildExpandedColumns, getGroupFieldValue, getGroupInstanceIds } from './form-responses-utils';
import { SummaryTab } from './SummaryTab';
import { TableTab } from './TableTab';
import { IndividualTab } from './IndividualTab';
import { QuestionsTab } from './QuestionsTab';
import { SettingsTab } from './SettingsTab';
import { FormWizardDialog } from './FormWizardDialog';
import { DeleteSubmissionDialog } from './DeleteSubmissionDialog';
import { PrintContent } from './PrintContent';

const SUBMISSION_NUMBER_FIELD_ID = '__submissionNumber';
const SUBMISSION_NUMBER_FIELD_NAME = 'Submission Number';

const ADDRESS_PART_OPTIONS: Array<{
  suffix: '' | 'street' | 'suburb' | 'state' | 'postcode' | 'country';
  label: string;
}> = [
  { suffix: '', label: 'Full address' },
  { suffix: 'street', label: 'Street' },
  { suffix: 'suburb', label: 'Suburb / City' },
  { suffix: 'state', label: 'State' },
  { suffix: 'postcode', label: 'Postcode' },
  { suffix: 'country', label: 'Country' },
];

const splitAddressTaskFieldId = (taskFieldId: string): { baseId: string; part: string | null } => {
  const idx = taskFieldId.indexOf(':');
  if (idx === -1) return { baseId: taskFieldId, part: null };
  return { baseId: taskFieldId.slice(0, idx), part: taskFieldId.slice(idx + 1) };
};

const ADDRESS_PART_TO_FIELD: Record<string, string> = {
  street: 'addressStreet',
  suburb: 'addressSuburb',
  state: 'addressState',
  postcode: 'addressPostcode',
  country: 'addressCountry',
};

const resolveFieldValue = (submissionFieldValues: any[], taskFieldId: string): string | undefined => {
  const { baseId, part } = splitAddressTaskFieldId(taskFieldId);
  const fieldValue = submissionFieldValues.find((fv) => fv.taskFieldId === baseId);
  if (!fieldValue) return undefined;
  if (part === null) return fieldValue.value || undefined;
  const propName = ADDRESS_PART_TO_FIELD[part];
  if (propName) {
    const subValue = fieldValue[propName];
    return typeof subValue === 'string' && subValue !== '' ? subValue : undefined;
  }
  return fieldValue.value || undefined;
};

interface ResolvedLocation {
  description: string;
  formatted: string;
  placesId: string;
  latitude: number;
  longitude: number;
}

const resolveLocationFromField = (submissionFieldValues: any[], taskFieldId: string): ResolvedLocation | undefined => {
  const { baseId } = splitAddressTaskFieldId(taskFieldId);
  const fieldValue = submissionFieldValues.find((fv) => fv.taskFieldId === baseId);
  if (!fieldValue) return undefined;
  const formatted = fieldValue.addressFormatted || fieldValue.value || '';
  if (!formatted) return undefined;
  return {
    description: fieldValue.value || formatted,
    formatted,
    placesId: fieldValue.placesId || '',
    latitude: fieldValue.latitude ?? 0,
    longitude: fieldValue.longitude ?? 0,
  };
};

const useTabClasses = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      boxSizing: 'border-box',
      minWidth: 72,
      textTransform: 'none',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      '&:hover': {
        color: theme.palette.primary.light,
        opacity: 1,
      },
      '&$selected': {
        color: theme.palette.primary.main,
      },
      '&:focus': {
        color: theme.palette.primary.main,
      },
    },
    selected: {},
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.color.white.neutral50,
    },
    header: {
      padding: '16px 32px 24px 32px',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
      backgroundColor: '#fff',
    },
    backButton: {
      color: theme.color.grey.neutral300,
      textTransform: 'none',
      padding: '4px 0',
      marginBottom: '8px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.02em',
    },
    subtitle: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
      marginTop: '4px',
    },
    tabsContainer: {
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
      backgroundColor: '#fff',
      paddingLeft: '16px',
    },
    content: {
      flex: 1,
      padding: '32px',
      overflowY: 'auto',
    },
    toolbarButton: {
      backgroundColor: 'transparent',
      color: '#646464',
      border: '1px solid #d9d9d9',
      borderRadius: '4px',
      padding: '4.5px 12px',
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'none',
      minWidth: 'auto',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
  })
);

export const FormResponsesScreen: React.FC = () => {
  const classes = useStyles();
  const tabClasses = useTabClasses();
  const history = useHistory();
  const { setSnackbar } = useSnackbar();
  const orgInfo = useOrgInfo();
  const apiUrl = useApiUrl();
  const [formId] = useQueryParam('fid', StringParam);
  const [tabParam] = useQueryParam('tab', StringParam);
  const printRef = useRef<HTMLDivElement>(null);

  // --- State ---
  const [currentTab, setCurrentTab] = useState(() => {
    if (tabParam === 'settings') return 4;
    if (tabParam === 'questions') return 3;
    if (tabParam === 'individual') return 2;
    if (tabParam === 'table') return 1;
    return 0;
  });
  const [page, setPage] = useState(0);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardSubmissionId, setWizardSubmissionId] = useState<string | null>(null);
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<string | null>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [savingMappings, setSavingMappings] = useState(false);
  const [genericSessionCount, setGenericSessionCount] = useState(1);

  // --- Queries ---
  const { data, loading, refetch: refetchForms } = useCampfireQuery<{ vm: { forms: FormType[] } }, undefined>(
    GET_FORMS,
    { options: { skip: !formId } }
  );

  const { data: listData, loading: listLoading, refetch: refetchList } = useCampfireQuery<
    GetFormSubmissionsList,
    GetFormSubmissionsListVariables
  >(GET_FORM_SUBMISSIONS_LIST, {
    options: {
      skip: !formId || currentTab === 0,
      variables: {
        formId: formId!,
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        sortOrder: sortOrder === 'newest' ? 'DESC' : 'ASC',
      },
    },
  });

  const { data: summaryData, loading: summaryLoading } = useCampfireQuery<
    GetFormSubmissionsList,
    GetFormSubmissionsListVariables
  >(GET_FORM_SUBMISSIONS_LIST, {
    options: {
      skip: !formId || (currentTab !== 0 && currentTab !== 3),
      variables: {
        formId: formId!,
        offset: 0,
        limit: 100,
        sortOrder: 'DESC',
      },
    },
  });

  const { data: programsActivitiesData } = useCampfireQuery<
    CreateRosterTemplateScreenGetProgramsAndActivities,
    undefined
  >(CREATE_ROSTER_TEMPLATE_SCREEN_GET_PROGRAMS_AND_ACTIVITIES);

  const programs = programsActivitiesData?.vm?.programs || [];
  const allActivities = programsActivitiesData?.vm?.activities || [];

  const { data: submissionData, loading: submissionLoading, refetch: refetchSubmission } = useCampfireQuery<
    GetFormSubmission,
    GetFormSubmissionVariables
  >(GET_FORM_SUBMISSION, {
    options: {
      skip: !formId || !selectedSubmissionId,
      variables: { formId: formId!, submissionId: selectedSubmissionId! },
    },
  });

  // --- Starred form fields ---
  const { user } = useUser();
  const { profileId } = user;

  const { data: starredData, refetch: refetchStarred } = useCampfireQuery<
    { vm: { profile: { userPreference: { starredFormFields: string | null } | null } | null } },
    { profileId: string }
  >(GET_STARRED_FORM_FIELDS, {
    options: {
      skip: !profileId,
      variables: { profileId: profileId! },
    },
  });

  const serverStarredFieldIds: string[] = useMemo(() => {
    if (!formId) return [];
    const raw = starredData?.vm?.profile?.userPreference?.starredFormFields;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return parsed[formId] || [];
    } catch {
      return [];
    }
  }, [starredData, formId]);

  const [localStarredFieldIds, setLocalStarredFieldIds] = useState<string[] | null>(null);
  const starredFieldIds = localStarredFieldIds ?? serverStarredFieldIds;

  useEffect(() => {
    setLocalStarredFieldIds(null);
  }, [serverStarredFieldIds]);

  const starredFormFieldsFetch = useCampfireFetch({ defer: true });

  const handleToggleStar = useCallback(
    (taskFieldId: string) => {
      if (!formId || !profileId) return;
      const isStarred = starredFieldIds.includes(taskFieldId);
      const newStarred = isStarred
        ? starredFieldIds.filter((id) => id !== taskFieldId)
        : [...starredFieldIds, taskFieldId];

      setLocalStarredFieldIds(newStarred);

      starredFormFieldsFetch
        .run({
          url: `${apiUrl}/vm/volunteer/preferences/starred-form-fields`,
          method: 'POST',
          data: { profileId, formId, starredFieldIds: newStarred },
        })
        .then(() => {
          if (refetchStarred) refetchStarred();
        })
        .catch(() => {
          setLocalStarredFieldIds(null);
          setSnackbar({ open: true, message: 'Failed to update starred fields', variant: 'error' });
        });
    },
    [formId, profileId, starredFieldIds, apiUrl, starredFormFieldsFetch, refetchStarred, setSnackbar]
  );

  const { data: wizardSubmissionData } = useCampfireQuery<GetFormSubmission, GetFormSubmissionVariables>(
    GET_FORM_SUBMISSION,
    {
      options: {
        skip: !formId || !wizardSubmissionId,
        variables: { formId: formId!, submissionId: wizardSubmissionId! },
      },
    }
  );

  // Prefetch adjacent submissions for navigation
  const submissions: FormSubmissionListItem[] = listData?.vm?.form?.formSubmissions || [];
  const prevSubmissionId = useMemo(
    () => (currentIndex > 0 ? submissions[currentIndex - 1]?.formSubmissionId || null : null),
    [submissions, currentIndex]
  );
  const nextSubmissionId = useMemo(
    () => (currentIndex < submissions.length - 1 ? submissions[currentIndex + 1]?.formSubmissionId || null : null),
    [submissions, currentIndex]
  );

  useCampfireQuery<GetFormSubmission, GetFormSubmissionVariables>(GET_FORM_SUBMISSION, {
    options: {
      skip: !formId || !prevSubmissionId,
      variables: { formId: formId!, submissionId: prevSubmissionId! },
    },
  });
  useCampfireQuery<GetFormSubmission, GetFormSubmissionVariables>(GET_FORM_SUBMISSION, {
    options: {
      skip: !formId || !nextSubmissionId,
      variables: { formId: formId!, submissionId: nextSubmissionId! },
    },
  });

  // --- Fetch helpers ---
  const deleteSubmissionFetch = useCampfireFetch({ defer: true });
  const saveFormSubmissionCreationFetch = useCampfireFetch({ defer: true });
  const saveFormFetch = useCampfireFetch({ defer: true });
  const setSubmissionStatusFetch = useCampfireFetch({ defer: true });

  // --- Derived data ---
  const orgLogo = (data as any)?.orgLogo as string | null | undefined;
  const forms = data?.vm?.forms || [];
  const form = forms.find((f) => f.formId === formId);
  const submissionCount = form?.submissionStats?.totalSubmissions || 0;
  const totalSubmissions = listData?.vm?.form?.submissionStats?.totalSubmissions || submissionCount;
  const totalPages = Math.ceil(totalSubmissions / PAGE_SIZE);
  const selectedSubmission = submissionData?.vm?.form?.formSubmission || null;

  const submissionNumbersEnabled = Boolean(fieldMappings['submissionNumbers.enabled']);

  const availableFormFields: AvailableFormField[] = useMemo(() => {
    const formItems = form?.formItems || [];
    const result: AvailableFormField[] = [];
    [...formItems]
      .sort((a, b) => a.order - b.order)
      .forEach((item) => {
        if (item.__typename === 'VOLUNTEER_FormItemFieldType') {
          const fieldType = item.field.__typename;
          if (fieldType === 'VOLUNTEER_AddressTaskFieldType') {
            ADDRESS_PART_OPTIONS.forEach(({ suffix, label }) => {
              result.push({
                taskFieldId: suffix === '' ? item.field.taskFieldId : `${item.field.taskFieldId}:${suffix}`,
                name: `${item.field.name} — ${label}`,
                fieldType,
              });
            });
          } else {
            result.push({
              taskFieldId: item.field.taskFieldId,
              name: item.field.name,
              fieldType,
            });
          }
        }
      });
    if (submissionNumbersEnabled) {
      result.push({ taskFieldId: SUBMISSION_NUMBER_FIELD_ID, name: SUBMISSION_NUMBER_FIELD_NAME });
    }
    return result;
  }, [form, submissionNumbersEnabled]);

  const formFields: FormFieldConfig[] = useMemo(() => {
    const formItems = listData?.vm?.form?.formItems || [];
    const result: FormFieldConfig[] = [];

    const sortedItems = [...formItems].sort((a, b) => a.order - b.order);

    sortedItems.forEach((item) => {
      if (item.__typename === 'VOLUNTEER_FormItemFieldType') {
        result.push({
          taskFieldId: item.field.taskFieldId,
          name: item.field.name,
          fieldType: item.field.__typename,
          ...('minValue' in item.field ? { minValue: item.field.minValue } : {}),
          ...('maxValue' in item.field ? { maxValue: item.field.maxValue } : {}),
          ...('minLabel' in item.field ? { minLabel: item.field.minLabel } : {}),
          ...('maxLabel' in item.field ? { maxLabel: item.field.maxLabel } : {}),
        });
      } else if (item.__typename === 'VOLUNTEER_FormItemRepeatableGroupType') {
        const groupItems = [...(item.groupItems || [])].sort((a, b) => a.order - b.order);
        groupItems.forEach((gi) => {
          if (gi.__typename === 'VOLUNTEER_FormItemFieldType') {
            result.push({
              taskFieldId: gi.field.taskFieldId,
              name: gi.field.name,
              fieldType: gi.field.__typename,
              ...('minValue' in gi.field ? { minValue: (gi.field as any).minValue } : {}),
              ...('maxValue' in gi.field ? { maxValue: (gi.field as any).maxValue } : {}),
              ...('minLabel' in gi.field ? { minLabel: (gi.field as any).minLabel } : {}),
              ...('maxLabel' in gi.field ? { maxLabel: (gi.field as any).maxLabel } : {}),
              groupFormItemId: item.formItemId,
              groupTitle: item.title || item.label || 'Group',
              groupLabel: item.label || undefined,
            });
          }
        });
      }
    });

    return result;
  }, [listData]);

  const fieldConfigMap = useMemo(() => new Map(formFields.map((f) => [f.taskFieldId, f])), [formFields]);

  const chartData: ChartDataPoint[] = useMemo(() => {
    const summarySubmissions = summaryData?.vm?.form?.formSubmissions || [];
    if (summarySubmissions.length === 0) return [];
    const dateMap: Record<string, number> = {};
    summarySubmissions.forEach((submission) => {
      const dt = unpackToDateTime(submission.submissionDate);
      const dateKey = dt.toFormat('MMM d');
      dateMap[dateKey] = (dateMap[dateKey] || 0) + 1;
    });
    return Object.entries(dateMap)
      .map(([date, count]) => ({ date, responses: count }))
      .reverse();
  }, [summaryData]);

  const recentSubmissions = useMemo(() => {
    const summarySubmissions = summaryData?.vm?.form?.formSubmissions || [];
    return summarySubmissions.slice(0, 5);
  }, [summaryData]);

  const fieldAnalytics: FieldAnalytics[] = useMemo(() => {
    const summarySubmissions = summaryData?.vm?.form?.formSubmissions || [];
    const formItems = summaryData?.vm?.form?.formItems || [];
    if (summarySubmissions.length === 0 || formItems.length === 0) return [];

    const fieldOrder: Array<{
      taskFieldId: string;
      name: string;
      fieldType: string;
      config: any;
      isGroupField: boolean;
    }> = [];
    [...formItems]
      .sort((a, b) => a.order - b.order)
      .forEach((item) => {
        if (item.__typename === 'VOLUNTEER_FormItemFieldType') {
          fieldOrder.push({
            taskFieldId: item.field.taskFieldId,
            name: item.field.name,
            fieldType: item.field.__typename,
            config: item.field,
            isGroupField: false,
          });
        } else if (item.__typename === 'VOLUNTEER_FormItemRepeatableGroupType') {
          const groupTitle = item.title || item.label || 'Group';
          [...(item.groupItems || [])]
            .sort((a, b) => a.order - b.order)
            .forEach((gi) => {
              if (gi.__typename === 'VOLUNTEER_FormItemFieldType') {
                fieldOrder.push({
                  taskFieldId: gi.field.taskFieldId,
                  name: `${groupTitle} - ${gi.field.name}`,
                  fieldType: gi.field.__typename,
                  config: gi.field,
                  isGroupField: true,
                });
              }
            });
        }
      });

    return fieldOrder.map(
      (field): FieldAnalytics => {
        const values: string[] = [];
        let submissionsWithAnswer = 0;
        const { isGroupField } = field;
        summarySubmissions.forEach((submission) => {
          const matchingValues = submission.formFieldValues?.filter((v) => v.taskFieldId === field.taskFieldId) || [];
          const submissionValues: string[] = [];
          matchingValues.forEach((fv) => {
            if (fv?.value && fv.value !== '-') {
              submissionValues.push(fv.value);
            }
          });
          if (submissionValues.length > 0) {
            submissionsWithAnswer++;
            if (isGroupField) {
              values.push(submissionValues.join(', '));
            } else {
              values.push(...submissionValues);
            }
          }
        });

        const totalResponses = summarySubmissions.length;
        const answeredCount = submissionsWithAnswer;
        const base = {
          taskFieldId: field.taskFieldId,
          name: field.name,
          fieldType: field.fieldType,
          totalResponses,
          answeredCount,
        };

        const isChoiceField =
          field.fieldType === 'VOLUNTEER_MultipleChoiceTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_FormMultipleChoiceTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_DropdownTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_FormDropdownTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_QuizTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_FormQuizTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_CheckboxTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_FormCheckboxTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_YesNoTaskFieldType';

        const isCheckboxField =
          field.fieldType === 'VOLUNTEER_CheckboxTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_FormCheckboxTaskFieldType';

        if (isChoiceField) {
          const counts: Record<string, number> = {};
          values.forEach((v) => {
            if (isCheckboxField) {
              v.split(', ').forEach((opt) => {
                counts[opt] = (counts[opt] || 0) + 1;
              });
            } else if (field.fieldType === 'VOLUNTEER_YesNoTaskFieldType') {
              const label = v === 'true' || v === '1' ? 'Yes' : 'No';
              counts[label] = (counts[label] || 0) + 1;
            } else {
              counts[v] = (counts[v] || 0) + 1;
            }
          });
          const choiceData = Object.entries(counts)
            .map(([name, count]) => ({
              name: name.length > 25 ? `${name.substring(0, 25)}...` : name,
              fullName: name,
              count,
              percentage: Math.round((count / answeredCount) * 100),
            }))
            .sort((a, b) => b.count - a.count);
          return { ...base, type: 'choice', data: choiceData };
        }

        if (
          field.fieldType === 'VOLUNTEER_RatingTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_NpsTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_LinearScaleTaskFieldType'
        ) {
          const numericValues = values.map((v) => parseFloat(v)).filter((v) => !Number.isNaN(v));
          const avg = numericValues.length > 0 ? numericValues.reduce((a, b) => a + b, 0) / numericValues.length : 0;
          const distribution: Record<number, number> = {};
          numericValues.forEach((v) => {
            distribution[v] = (distribution[v] || 0) + 1;
          });

          let scaleMin = 1;
          let scaleMax = 5;
          if (field.fieldType === 'VOLUNTEER_NpsTaskFieldType') {
            scaleMin = 0;
            scaleMax = 10;
          } else if (field.config.__typename === 'VOLUNTEER_LinearScaleTaskFieldType') {
            scaleMin = field.config.minValue ?? 1;
            scaleMax = field.config.maxValue ?? 10;
          }

          const numericData = [];
          for (let i = scaleMin; i <= scaleMax; i++) {
            numericData.push({ name: String(i), count: distribution[i] || 0 });
          }
          return { ...base, type: 'numeric', avg: avg.toFixed(1), data: numericData, scaleMin, scaleMax };
        }

        if (
          field.fieldType === 'VOLUNTEER_ShortTextTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_LongTextTaskFieldType'
        ) {
          const textItems = values.slice(0, 20).map((v, i) => ({ id: `${field.taskFieldId}-text-${i}`, value: v }));
          return { ...base, type: 'text', items: textItems };
        }

        if (
          field.fieldType === 'VOLUNTEER_EmailTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_PhoneTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_UrlTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_AddressTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_DateTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_TimeTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_NumberTaskFieldType'
        ) {
          const listItems = values.slice(0, 20).map((v, i) => ({ id: `${field.taskFieldId}-list-${i}`, value: v }));
          return { ...base, type: 'list', items: listItems };
        }

        if (field.fieldType === 'VOLUNTEER_SignatureTaskFieldType') {
          return { ...base, type: 'signature' };
        }

        if (
          field.fieldType === 'VOLUNTEER_MatrixTaskFieldType' ||
          field.fieldType === 'VOLUNTEER_FormMatrixTaskFieldType'
        ) {
          const matrixItems = values.slice(0, 10).map((v, i) => ({ id: `${field.taskFieldId}-matrix-${i}`, value: v }));
          return { ...base, type: 'matrix', items: matrixItems };
        }

        return { ...base, type: 'unknown', values };
      }
    );
  }, [summaryData]);

  // --- Wizard prefill values ---
  const defaultProgramId = fieldMappings['rosterTemplate.defaultProgramId'] || '';
  const defaultActivityId = fieldMappings['rosterTemplate.defaultActivityId'] || '';

  const activitiesForProgram = useMemo(() => {
    if (!defaultProgramId) return allActivities;
    return allActivities.filter((activity) => activity.program?.programId === defaultProgramId);
  }, [allActivities, defaultProgramId]);

  const sessionsForActivity = useMemo(() => {
    if (!defaultActivityId) return [];
    const activity = allActivities.find((a) => a.activityId === defaultActivityId);
    return activity?.sessions || [];
  }, [allActivities, defaultActivityId]);

  const activityPrefilledValues = useMemo(() => {
    if (!wizardSubmissionData || !fieldMappings) return undefined;
    const submission = wizardSubmissionData?.vm?.form?.formSubmission;
    const submissionFieldValues = submission?.formFieldValues || [];
    const submissionNumber = submission?.submissionNumber;

    const getFieldVal = (mappingKey: string): string | undefined => {
      const taskFieldId = fieldMappings[mappingKey];
      if (!taskFieldId) return undefined;
      if (taskFieldId === SUBMISSION_NUMBER_FIELD_ID) {
        return submissionNumber != null ? String(submissionNumber) : undefined;
      }
      return resolveFieldValue(submissionFieldValues, taskFieldId);
    };

    const vals: Record<string, any> = {};
    const activityName = getFieldVal('activity.activityName');
    if (activityName) vals.activityName = activityName;
    const activityDescription = getFieldVal('activity.activityDescription');
    if (activityDescription) vals.activityDescription = activityDescription;
    const maxTeamStr = getFieldVal('activity.maxTeam');
    if (maxTeamStr) {
      const maxTeam = parseInt(maxTeamStr, 10);
      if (!Number.isNaN(maxTeam)) vals.maxTeam = maxTeam;
    }
    const activityLocationFieldId =
      fieldMappings['activity.location.formatted'] || fieldMappings['activity.location.description'];
    if (activityLocationFieldId) {
      const loc = resolveLocationFromField(submissionFieldValues, activityLocationFieldId);
      if (loc) {
        vals.location = { ...loc, activityLocationId: '' };
      }
    }
    const remoteDetails = getFieldVal('activity.remoteLocation.details');
    if (remoteDetails) vals.remoteLocation = { details: remoteDetails };

    return Object.keys(vals).length > 0 ? vals : undefined;
  }, [wizardSubmissionData, fieldMappings]);

  const rosterTemplatePrefilledValues = useMemo(() => {
    const vals: Record<string, any> = {};
    if (defaultProgramId) vals.programId = defaultProgramId;
    if (defaultActivityId) vals.activityId = defaultActivityId;
    if (!wizardSubmissionData || !fieldMappings) {
      return Object.keys(vals).length > 0 ? vals : undefined;
    }

    const submission = wizardSubmissionData?.vm?.form?.formSubmission;
    const submissionFieldValues = submission?.formFieldValues || [];
    const submissionNumber = submission?.submissionNumber;

    const getFieldVal = (mappingKey: string): string | undefined => {
      const taskFieldId = fieldMappings[mappingKey];
      if (!taskFieldId) return undefined;
      if (taskFieldId === SUBMISSION_NUMBER_FIELD_ID) {
        return submissionNumber != null ? String(submissionNumber) : undefined;
      }
      return resolveFieldValue(submissionFieldValues, taskFieldId);
    };

    const replaceMergeTagsInTemplate = (template: string): string =>
      template.replace(/\{\{([^}]+)\}\}/g, (_unusedMatch, raw) => {
        const trimmedId = raw.trim();
        if (trimmedId === SUBMISSION_NUMBER_FIELD_ID) {
          return submissionNumber != null ? String(submissionNumber) : '';
        }
        return resolveFieldValue(submissionFieldValues, trimmedId) ?? '';
      });

    const rosterNameTemplate = fieldMappings['rosterTemplate.rosterName'];
    if (rosterNameTemplate) {
      const rosterName = replaceMergeTagsInTemplate(rosterNameTemplate);
      if (rosterName.trim()) vals.rosterName = rosterName;
    }
    const rosterNotesMapping = fieldMappings['rosterTemplate.rosterNotes'];
    if (rosterNotesMapping) {
      if (rosterNotesMapping.includes('{{')) {
        const resolved = replaceMergeTagsInTemplate(rosterNotesMapping);
        const strippedText = resolved.replace(/<[^>]*>/g, '').trim();
        if (strippedText) vals.rosterNotes = resolved;
      } else {
        vals.rosterNotes = rosterNotesMapping;
      }
    }
    const rosterDate = getFieldVal('rosterTemplate.date');
    if (rosterDate) {
      const parsed = DateTime.fromISO(rosterDate);
      if (parsed.isValid) {
        vals.rosterDates = [parsed.toJSDate()];
      }
    }

    const rosterLocationFieldId =
      fieldMappings['rosterTemplate.location.formatted'] || fieldMappings['rosterTemplate.location.description'];
    if (rosterLocationFieldId) {
      const loc = resolveLocationFromField(submissionFieldValues, rosterLocationFieldId);
      if (loc) {
        vals.location = loc;
      }
    }

    if (sessionsForActivity.length > 0) {
      const flexibleSessionConfigs: Array<{
        sessionId: string;
        customName: string;
        startTime: string;
        endTime: string;
      }> = [];
      sessionsForActivity.forEach((session: any) => {
        const customName = getFieldVal(`session.${session.sessionId}.customName`);
        const startTime = getFieldVal(`session.${session.sessionId}.startTime`);
        const endTime = getFieldVal(`session.${session.sessionId}.endTime`);
        if (customName || startTime || endTime) {
          flexibleSessionConfigs.push({
            sessionId: session.sessionId,
            customName: customName || '',
            startTime: startTime || session.startTime || '',
            endTime: endTime || session.endTime || '',
          });
        }
      });
      if (flexibleSessionConfigs.length > 0) vals.flexibleSessionConfigs = flexibleSessionConfigs;
    } else {
      const positionalSessionConfigs: Array<{
        index: number;
        customName: string;
        startTime: string;
        endTime: string;
      }> = [];
      [0, 1, 2].forEach((index) => {
        const customName = getFieldVal(`session.${index}.customName`);
        const startTime = getFieldVal(`session.${index}.startTime`);
        const endTime = getFieldVal(`session.${index}.endTime`);
        if (customName || startTime || endTime) {
          positionalSessionConfigs.push({
            index,
            customName: customName || '',
            startTime: startTime || '',
            endTime: endTime || '',
          });
        }
      });
      if (positionalSessionConfigs.length > 0) vals.positionalSessionConfigs = positionalSessionConfigs;
    }

    return Object.keys(vals).length > 0 ? vals : undefined;
  }, [wizardSubmissionData, fieldMappings, defaultProgramId, defaultActivityId, sessionsForActivity]);

  const emailPrefilledValues = useMemo(() => {
    if (!wizardSubmissionData || !fieldMappings) return undefined;
    const submission = wizardSubmissionData?.vm?.form?.formSubmission;
    const submissionFieldValues = submission?.formFieldValues || [];
    const submissionNumber = submission?.submissionNumber;

    const replaceMergeTags = (template: string): string =>
      template.replace(/\{\{([^}]+)\}\}/g, (_unusedMatch, raw) => {
        const trimmedId = raw.trim();
        if (trimmedId === SUBMISSION_NUMBER_FIELD_ID) {
          return submissionNumber != null ? String(submissionNumber) : '';
        }
        return resolveFieldValue(submissionFieldValues, trimmedId) ?? '';
      });

    const vals: Record<string, any> = {};
    const subjectTemplate = fieldMappings['email.subject'];
    if (subjectTemplate) {
      const subject = replaceMergeTags(subjectTemplate);
      if (subject.trim()) vals.subject = subject;
    }
    const messageTemplate = fieldMappings['email.message'];
    if (messageTemplate) {
      vals.message = replaceMergeTags(messageTemplate);
    }

    return Object.keys(vals).length > 0 ? vals : undefined;
  }, [wizardSubmissionData, fieldMappings]);

  const calendarInvitePrefilledValues = useMemo(() => {
    if (!wizardSubmissionData || !fieldMappings) return undefined;
    const submission = wizardSubmissionData?.vm?.form?.formSubmission;
    const submissionFieldValues = submission?.formFieldValues || [];
    const submissionNumber = submission?.submissionNumber;

    const getFieldVal = (mappingKey: string): string | undefined => {
      const taskFieldId = fieldMappings[mappingKey];
      if (!taskFieldId) return undefined;
      if (taskFieldId === SUBMISSION_NUMBER_FIELD_ID) {
        return submissionNumber != null ? String(submissionNumber) : undefined;
      }
      return resolveFieldValue(submissionFieldValues, taskFieldId);
    };

    const replaceMergeTags = (template: string): string =>
      template.replace(/\{\{([^}]+)\}\}/g, (_unusedMatch, raw) => {
        const trimmedId = raw.trim();
        if (trimmedId === SUBMISSION_NUMBER_FIELD_ID) {
          return submissionNumber != null ? String(submissionNumber) : '';
        }
        return resolveFieldValue(submissionFieldValues, trimmedId) ?? '';
      });

    const vals: Record<string, any> = {};
    const attendeeEmail = getFieldVal('calendarInvite.attendeeEmail');
    if (attendeeEmail) vals.attendeeEmail = attendeeEmail;
    const title = getFieldVal('calendarInvite.title');
    if (title) vals.title = title;
    const date = getFieldVal('calendarInvite.date');
    if (date) vals.date = date;
    const startTime = getFieldVal('calendarInvite.startTime');
    if (startTime) vals.startTime = startTime;
    const endTime = getFieldVal('calendarInvite.endTime');
    if (endTime) vals.endTime = endTime;
    const meetingType = getFieldVal('calendarInvite.meetingType');
    if (meetingType) vals.meetingType = meetingType;
    const calendarLocationFieldId =
      fieldMappings['calendarInvite.location.formatted'] || fieldMappings['calendarInvite.location.description'];
    if (calendarLocationFieldId) {
      const loc = resolveLocationFromField(submissionFieldValues, calendarLocationFieldId);
      if (loc) {
        vals.location = loc;
      }
    }
    const meetingLink = getFieldVal('calendarInvite.meetingLink');
    if (meetingLink) vals.meetingLink = meetingLink;
    const phoneNumber = getFieldVal('calendarInvite.phoneNumber');
    if (phoneNumber) vals.phoneNumber = phoneNumber;
    const notesTemplate = fieldMappings['calendarInvite.notes'];
    if (notesTemplate) {
      vals.notes = replaceMergeTags(notesTemplate);
    }

    return Object.keys(vals).length > 0 ? vals : undefined;
  }, [wizardSubmissionData, fieldMappings]);

  // --- Effects ---
  useEffect(() => {
    if (form?.fieldMappings) {
      try {
        const parsed = typeof form.fieldMappings === 'string' ? JSON.parse(form.fieldMappings) : form.fieldMappings;
        setFieldMappings(parsed);
      } catch {
        setFieldMappings({});
      }
    }
  }, [form?.fieldMappings]);

  useEffect(() => {
    if (currentTab === 2 && submissions.length > 0 && !selectedSubmissionId) {
      setSelectedSubmissionId(submissions[0].formSubmissionId);
      setCurrentIndex(0);
    }
  }, [currentTab, submissions, selectedSubmissionId]);

  useEffect(() => {
    if (submissions.length > 0 && currentIndex < submissions.length) {
      setSelectedSubmissionId(submissions[currentIndex].formSubmissionId);
    }
  }, [submissions, currentIndex]);

  // --- Handlers ---
  const handleDeleteSubmission = () => {
    if (!submissionToDelete) return;
    deleteSubmissionFetch
      .run({
        url: `${apiUrl}/vm/form/submission/remove`,
        method: 'POST',
        data: { formSubmissionId: submissionToDelete },
      })
      .then(() => {
        setDeleteDialogOpen(false);
        setSubmissionToDelete(null);
        setSnackbar({ open: true, message: 'Submission removed successfully', variant: 'success' });
        if (refetchForms) refetchForms();
      })
      .catch(() => {
        setSnackbar({ open: true, message: 'Failed to remove submission', variant: 'error' });
      });
  };

  const handleEntityCreated = useCallback(
    (entityType: EntityType, entityId: string) => {
      if (!wizardSubmissionId) return;
      saveFormSubmissionCreationFetch
        .run({
          url: `${apiUrl}/vm/form/submission-creation/save`,
          method: 'POST',
          data: { formSubmissionId: wizardSubmissionId, entityType, entityId },
        })
        .catch((error) => console.error('Failed to save entity creation:', error));
    },
    [wizardSubmissionId, apiUrl, saveFormSubmissionCreationFetch]
  );

  const handleSaveFieldMappings = useCallback(() => {
    if (!form) return;
    setSavingMappings(true);
    saveFormFetch
      .run({
        url: `${apiUrl}/vm/form/save`,
        method: 'POST',
        data: {
          formId: form.formId,
          managerId: form.manager.managerId,
          title: form.title,
          description: form.description,
          successMessage: form.successMessage,
          isListed: form.isListed,
          allowMultipleSubmissions: form.allowMultipleSubmissions || false,
          requiresApproval: (form as any).requiresApproval || false,
          fieldMappings,
        },
      })
      .then((res) => {
        setSavingMappings(false);
        if (res.ok) {
          setSnackbar({ open: true, message: 'Settings saved', variant: 'success' });
        } else {
          setSnackbar({ open: true, message: 'Failed to save settings', variant: 'error' });
        }
      })
      .catch(() => {
        setSavingMappings(false);
        setSnackbar({ open: true, message: 'Failed to save settings', variant: 'error' });
      });
  }, [form, fieldMappings, apiUrl, saveFormFetch, setSnackbar]);

  const handleExport = useCallback(() => {
    const allSubmissions = summaryData?.vm?.form?.formSubmissions || [];
    if (allSubmissions.length === 0) {
      setSnackbar({ open: true, message: 'No submissions to export', variant: 'warning' });
      return;
    }

    const expandedColumns = buildExpandedColumns(formFields, allSubmissions);
    const headers = ['#', 'Date', ...expandedColumns.map((f) => f.expandedName)];

    const groups = new Map<string, Set<string>>();
    formFields.forEach((f) => {
      if (f.groupFormItemId) {
        const existing = groups.get(f.groupFormItemId) || new Set<string>();
        existing.add(f.taskFieldId);
        groups.set(f.groupFormItemId, existing);
      }
    });

    const rows = allSubmissions.map((submission, index) => {
      const dt = unpackToDateTime(submission.submissionDate);
      const dateStr = dt.isValid ? dt.toFormat('d LLL yyyy, h:mm a') : '-';

      const groupInstanceOrder = new Map<string, string[]>();
      groups.forEach((fieldIds, groupFormItemId) => {
        groupInstanceOrder.set(groupFormItemId, getGroupInstanceIds(submission, fieldIds));
      });

      const fieldValues = expandedColumns.map((col) => {
        if (!col.groupFormItemId) {
          const fv = submission.formFieldValues?.find((v) => v.taskFieldId === col.taskFieldId);
          return fv?.value || '-';
        }
        const instances = groupInstanceOrder.get(col.groupFormItemId) || [];
        const instanceId = instances[col.instanceIndex];
        if (!instanceId) return '-';
        return getGroupFieldValue(submission, col.taskFieldId, instanceId);
      });
      return [index + 1, dateStr, ...fieldValues];
    });

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map((row) => row.map((cell) => escapeCSV(String(cell))).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${form?.title || 'form'}-responses-${DateTime.now().toFormat('yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [summaryData, formFields, form, setSnackbar]);

  const handleBack = () => history.push('/admin/forms');

  const handleTabChange = (_unusedEvent: React.ChangeEvent<{}>, newValue: number) => {
    setCurrentTab(newValue);
    if (newValue !== 1) setSelectedSubmissionId(null);
    setPage(0);
    setCurrentIndex(0);
  };

  const handleSortChange = (order: 'newest' | 'oldest') => {
    setSortOrder(order);
    setPage(0);
    setCurrentIndex(0);
    setSelectedSubmissionId(null);
  };

  const currentResponseNumber = page * PAGE_SIZE + currentIndex + 1;
  const hasPrevious = currentResponseNumber > 1;
  const hasNext = currentResponseNumber < totalSubmissions;

  const handlePrevResponse = () => {
    if (!hasPrevious) return;
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setSelectedSubmissionId(submissions[newIndex].formSubmissionId);
    } else if (page > 0) {
      setPage(page - 1);
      setCurrentIndex(PAGE_SIZE - 1);
    }
  };

  const handleNextResponse = () => {
    if (!hasNext) return;
    if (currentIndex < submissions.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setSelectedSubmissionId(submissions[newIndex].formSubmissionId);
    } else if (page < totalPages - 1) {
      setPage(page + 1);
      setCurrentIndex(0);
    }
  };

  const handleOpenWizard = (submissionId: string) => {
    setWizardSubmissionId(submissionId);
    setWizardStep(0);
    const defaultType = fieldMappings['wizard.defaultEntityType'] as EntityType | undefined;
    setSelectedEntityType(defaultType || null);
    setWizardOpen(true);
  };

  const handleWizardSuccess = () => {
    setWizardOpen(false);
    setWizardStep(0);
    setSelectedEntityType(null);
    setWizardSubmissionId(null);
    if (selectedSubmissionId) refetchSubmission();
  };

  const handleViewSubmission = (index: number, submissionId: string) => {
    setCurrentIndex(index);
    setSelectedSubmissionId(submissionId);
    setCurrentTab(2);
  };

  const handleViewComments = (index: number, submissionId: string) => {
    setCurrentIndex(index);
    setSelectedSubmissionId(submissionId);
    setCurrentTab(2);
  };

  const handleRequestDelete = (submissionId: string) => {
    setSubmissionToDelete(submissionId);
    setDeleteDialogOpen(true);
  };

  const handleSetSubmissionStatus = useCallback(
    (submissionId: string, status: 'approved' | 'declined' | null) => {
      setSubmissionStatusFetch
        .run({
          url: `${apiUrl}/vm/form/submission/set-status`,
          method: 'POST',
          data: { formSubmissionId: submissionId, status },
        })
        .then((res) => {
          if (res.ok) {
            setSnackbar({
              open: true,
              message:
                status === 'approved'
                  ? 'Submission approved'
                  : status === 'declined'
                  ? 'Submission declined'
                  : 'Submission status cleared',
              variant: 'success',
            });
            refetchList();
            if (selectedSubmissionId === submissionId) refetchSubmission();
          } else {
            setSnackbar({ open: true, message: 'Failed to update status', variant: 'error' });
          }
        })
        .catch(() => {
          setSnackbar({ open: true, message: 'Failed to update status', variant: 'error' });
        });
    },
    [apiUrl, setSubmissionStatusFetch, setSnackbar, selectedSubmissionId]
  );

  // --- Render ---
  if (loading) {
    return <CircularProgressOverlay isLoading />;
  }

  if (!form) {
    return (
      <Box className={classes.container}>
        <Box className={classes.header}>
          <TabletButton
            size='small'
            variant='text'
            startIcon={<ArrowBack style={{ fontSize: '18px' }} />}
            onClick={handleBack}
            className={classes.backButton}
          >
            Back to Forms
          </TabletButton>
          <Typography className={classes.title}>Form not found</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
          <Box>
            <TabletButton
              size='small'
              variant='text'
              startIcon={<ArrowBack style={{ fontSize: '18px' }} />}
              onClick={handleBack}
              className={classes.backButton}
            >
              Back to Forms
            </TabletButton>
            <Typography className={classes.title}>{form.title}</Typography>
            <Typography className={classes.subtitle}>
              {submissionCount} {submissionCount === 1 ? 'response' : 'responses'}
            </Typography>
          </Box>
          <Button
            startIcon={<Edit style={{ fontSize: '14px' }} />}
            className={classes.toolbarButton}
            onClick={() => history.push(`/admin/forms/form-builder?fid=${formId}`)}
          >
            Edit Fields
          </Button>
        </Box>
      </Box>

      <Box className={classes.tabsContainer}>
        <Tabs value={currentTab} onChange={handleTabChange} indicatorColor='primary'>
          <Tab disableRipple disableTouchRipple classes={tabClasses} label='Summary' />
          <Tab disableRipple disableTouchRipple classes={tabClasses} label='Table' />
          <Tab disableRipple disableTouchRipple classes={tabClasses} label='Individual' />
          <Tab disableRipple disableTouchRipple classes={tabClasses} label='Questions' />
          <Tab disableRipple disableTouchRipple classes={tabClasses} label='Settings' />
        </Tabs>
      </Box>

      <Box className={classes.content}>
        {currentTab === 0 && (
          <SummaryTab
            submissionCount={submissionCount}
            summaryLoading={summaryLoading}
            chartData={chartData}
            recentSubmissions={recentSubmissions}
            onViewSubmission={handleViewSubmission}
          />
        )}

        {currentTab === 1 && (
          <TableTab
            submissions={submissions}
            formFields={formFields}
            listLoading={listLoading}
            totalSubmissions={totalSubmissions}
            page={page}
            sortOrder={sortOrder}
            requiresApproval={!!(form as any)?.requiresApproval}
            onPageChange={setPage}
            onSortChange={handleSortChange}
            onViewSubmission={handleViewSubmission}
            onViewComments={handleViewComments}
            onOpenWizard={handleOpenWizard}
            onDeleteSubmission={handleRequestDelete}
            onSetStatus={handleSetSubmissionStatus}
            onExport={handleExport}
          />
        )}

        {currentTab === 2 && (
          <IndividualTab
            submissions={submissions}
            selectedSubmission={selectedSubmission}
            formFields={formFields}
            fieldConfigMap={fieldConfigMap}
            submissionCount={submissionCount}
            totalSubmissions={totalSubmissions}
            currentResponseNumber={currentResponseNumber}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            listLoading={listLoading}
            submissionLoading={submissionLoading}
            sortOrder={sortOrder}
            printRef={printRef}
            starredFieldIds={starredFieldIds}
            onToggleStar={handleToggleStar}
            onSortChange={handleSortChange}
            onPrevResponse={handlePrevResponse}
            onNextResponse={handleNextResponse}
            onOpenWizard={handleOpenWizard}
            onDeleteSubmission={handleRequestDelete}
            onSetStatus={handleSetSubmissionStatus}
            requiresApproval={!!(form as any)?.requiresApproval}
            onRefetchSubmission={refetchSubmission}
          />
        )}

        {currentTab === 3 && (
          <QuestionsTab
            submissionCount={submissionCount}
            summaryLoading={summaryLoading}
            fieldAnalytics={fieldAnalytics}
          />
        )}

        {currentTab === 4 && (
          <SettingsTab
            formId={formId || ''}
            form={form}
            fieldMappings={fieldMappings}
            availableFormFields={availableFormFields}
            programs={programs}
            allActivities={allActivities}
            activitiesForProgram={activitiesForProgram}
            sessionsForActivity={sessionsForActivity}
            genericSessionCount={genericSessionCount}
            savingMappings={savingMappings}
            onFieldMappingsChange={setFieldMappings}
            onSaveFieldMappings={handleSaveFieldMappings}
            onGenericSessionCountChange={setGenericSessionCount}
            onRefetchForms={refetchForms}
          />
        )}
      </Box>

      <PrintContent
        printRef={printRef}
        formTitle={form.title}
        selectedSubmission={selectedSubmission}
        currentResponseNumber={currentResponseNumber}
        totalSubmissions={totalSubmissions}
        formFields={formFields}
        orgLogo={orgLogo}
        orgName={orgInfo?.name}
      />

      <FormWizardDialog
        open={wizardOpen}
        wizardStep={wizardStep}
        selectedEntityType={selectedEntityType}
        activityPrefilledValues={activityPrefilledValues}
        rosterTemplatePrefilledValues={rosterTemplatePrefilledValues}
        emailPrefilledValues={emailPrefilledValues}
        calendarInvitePrefilledValues={calendarInvitePrefilledValues}
        onClose={() => setWizardOpen(false)}
        onStepChange={setWizardStep}
        onEntityTypeChange={setSelectedEntityType}
        onEntityCreated={handleEntityCreated}
        onSuccess={handleWizardSuccess}
      />

      <DeleteSubmissionDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteSubmission}
      />
    </Box>
  );
};
